/**
 * WYSIWYG editor component using TinyMCE (Free Version)
 * @module RichTextEditor
 */
import React, { useRef, useEffect, useCallback } from "react";
import { default as Editor } from "./common/TinyMCEBundle";

const EMPTY_CONTENT = "";

/**
 * Rich text editor component based on TinyMCE's free version
 * @param {Object} props - Component props
 * @param {string} props.data - Initial editor content
 * @param {Function} props.onChange - Change handler function
 */
const RichTextEditor = (props) => {
  const { data, onChange } = props;
  const currentData = data || EMPTY_CONTENT;
  const editorRef = useRef(null);
  const initialValueRef = useRef(currentData);
  const latestDataRef = useRef(currentData);
  const onChangeRef = useRef(onChange);
  const lastSyncedDataRef = useRef(currentData);
  const lastEditorChangeRef = useRef(null);

  // `data` is the form's persisted value, not the editor's live value. Keep
  // the latest value available for the async TinyMCE initialization callback.
  latestDataRef.current = currentData;
  onChangeRef.current = onChange;

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || lastSyncedDataRef.current === currentData) {
      return;
    }

    // A parent render caused by this editor's own onChange is an acknowledgement
    // of the value already in TinyMCE. Replacing the document here resets its
    // selection, even when the HTML is semantically equivalent.
    if (
      lastEditorChangeRef.current?.content === currentData &&
      lastEditorChangeRef.current?.previousData !== currentData
    ) {
      lastEditorChangeRef.current = null;
      lastSyncedDataRef.current = currentData;
      return;
    }

    if (editor.getContent() !== currentData) {
      // External loads/resets must not generate another change event.
      editor.setContent(currentData, { no_events: true });
    }
    lastEditorChangeRef.current = null;
    lastSyncedDataRef.current = currentData;
  }, [currentData]);

  // A controlled parent can reject an edit while re-rendering with the same
  // prior value (for example, after validation). The dependency-based effect
  // cannot observe that unchanged prop, so restore the persisted value here.
  useEffect(() => {
    const editor = editorRef.current;
    const pendingChange = lastEditorChangeRef.current;
    if (
      !editor ||
      pendingChange?.previousData !== currentData ||
      pendingChange.content === currentData
    ) {
      return;
    }

    if (editor.getContent() !== currentData) {
      editor.setContent(currentData, { no_events: true });
    }
    lastEditorChangeRef.current = null;
    lastSyncedDataRef.current = currentData;
  });

  // For compatibility with the previous API that used editor.getData()
  const handleEditorChange = useCallback((content) => {
    const nextContent = content || EMPTY_CONTENT;
    lastEditorChangeRef.current = {
      content: nextContent,
      previousData: latestDataRef.current,
    };

    if (onChangeRef.current) {
      // Create an object similar to CKEditor's structure to maintain backward compatibility
      const editorInstance = {
        getData: () => nextContent,
      };
      onChangeRef.current(null, editorInstance);
    }
  }, []);

  const handleEditorInit = useCallback((evt, editor) => {
    editorRef.current = editor;

    // The API can resolve before TinyMCE finishes initializing. In that race,
    // the value in the first render is stale, so apply the latest external
    // value once the editor is ready.
    const latestData = latestDataRef.current;
    if (editor.getContent() !== latestData) {
      editor.setContent(latestData, { no_events: true });
    }
    lastSyncedDataRef.current = latestData;
  }, []);

  // Merge default init with props.init
  const finalInit = {
    license_key: "gpl",
    height: 500, // Default height within RichTextEditor
    menubar: true,
    plugins: [
      "advlist",
      "anchor",
      "autolink",
      "help",
      "image",
      "link",
      "lists",
      "searchreplace",
      "table",
      "wordcount",
    ],
    toolbar:
      "undo redo | blocks | " +
      "bold italic forecolor | alignleft aligncenter " +
      "alignright alignjustify | bullist numlist outdent indent | " +
      "removeformat | help",
    content_style:
      "body { font-family:Helvetica,Arial,sans-serif; font-size:14px }",
    branding: false,
    promotion: false,
    ...(props.init || {}), // Spread props.init to override defaults, ensure props.init is an object
  };

  //log.debug("RichTextEditor final init object:", finalInit); // Keep for debugging if needed

  return (
    <div className="rich-text-editor-wrapper">
      <Editor
        onInit={handleEditorInit}
        initialValue={initialValueRef.current}
        onEditorChange={handleEditorChange}
        init={finalInit} // Use the constructed finalInit object
      />
    </div>
  );
};

export default RichTextEditor;
